
import { makeAutoObservable, runInAction } from 'mobx'
import { injectable, inject, decorate } from 'inversify'

// evertel
import { SessionService } from '@evertel/session/SessionService'
import { Api } from '@evertel/api'
import { APIDataUserSettings } from '@evertel/types'
import { UserSettingsStore } from '@evertel/stores'


class UserSettingsController {
    hasPushPermission: string | undefined

    constructor(
        private api: Api,
        private sessionService: SessionService,
        private userSettingsStore: UserSettingsStore
    ) {
        makeAutoObservable(this)
    }

    fetchUserSettings = async () => {
        if (!this.sessionService.currentUserId) return

        try {
            const userSettings = await this.api.Routes.BlueUser.getSettings(this.sessionService.currentUserId) as APIDataUserSettings

            runInAction(() => {
                this.userSettingsStore.update({ id: this.sessionService.currentUserId, settings: userSettings })
            })

        } catch (error: any) {
            console.error(error.message)
        }
    }

    updateUserRoomNotificationSettings = async (roomId: number, enabled: boolean) => {
        if (!roomId) return

        const userSettings = await this.api.Routes.BlueUser.putSettings(this.sessionService.currentUserId, {
            roomNotifications: {
                [roomId]: {
                    roomMessage: {
                        enabled
                    }
                }
            }
        }) as APIDataUserSettings

        runInAction(() => {
            this.userSettingsStore.update({ id: this.sessionService.currentUserId, settings: userSettings })
        })
    }

    updateUserNotificationSettings = async (settings: object) => {
        const userSettings = await this.api.Routes.BlueUser.putSettings(this.sessionService.currentUserId, settings)

        runInAction(() => {
            this.userSettingsStore.update({ id: this.sessionService.currentUserId, settings: userSettings })
        })

    }

    get settings() {
        return this.userSettingsStore.findById(this.sessionService.currentUserId)?.settings
    }

    get roomNotificationSettings() {
        return this.settings?.roomNotifications || {}
    }

    roomNotificationsEnabled = (roomId: number): boolean => {
        const settings = this.userSettingsStore.findById(this.sessionService.currentUserId)?.settings

        if (!settings?.roomNotifications?.[roomId]) {
            return true // Default to true if not set
        }

        return !!settings.roomNotifications[roomId].roomMessage?.enabled
    }

    get notificationsDisabled() {
        return this.settings?.disableNotifications
    }

    get notificationsEnabled() {
        return !this.settings?.disableNotifications
    }

    toggleAllNotifications = async () => {

        await this.updateUserNotificationSettings({
            disableNotifications: !this.notificationsDisabled
        })
    }

    destroy = () => {
        //the underlying UserSettingsStore is cleared via Store.clearAll()
    }

}

decorate(injectable(), UserSettingsController)
decorate(inject(Api), UserSettingsController, 0)
decorate(inject(SessionService), UserSettingsController, 1)
decorate(inject(UserSettingsStore), UserSettingsController, 2)

export { UserSettingsController }
